/** Custom Store Groups: gateway wrappers. Each checks and resolves organizationId (resolveStoreGroupOrganization); gateway errors propagate unchanged. */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import {
    getCallerOrganizationIds,
    getResolvedOrganizationId,
    initCurrentUser,
    isCurrentUserFailed,
} from '@api/userAndStoreContext'
import {
    GetStoreGroupGrantCountInput,
    GetStoreGroupGrantCountOutput,
    CreateStoreGroupInput,
    CreateStoreGroupOutput,
    DeleteStoreGroupInput,
    DeleteStoreGroupOutput,
    DetachStoreGroupInput,
    DetachStoreGroupOutput,
    GetStoreGroupInfoInput,
    GetStoreGroupInfoOutput,
    ListStoreGroupLeafStoresInput,
    ListStoreGroupLeafStoresOutput,
    ListStoreGroupsInput,
    ListStoreGroupsOutput,
    NestStoreGroupInput,
    NestStoreGroupOutput,
    UpdateStoreGroupInput,
    UpdateStoreGroupOutput,
    StoreGroupKind,
} from '@amzn/coral_com-amazon-jwostoremanagementgateway'

/** The model's StoreGroupKind values, as the tools send them over the cross-frame boundary. */
export type StoreGroupKindValue = `${StoreGroupKind}`

export interface IListStoreGroupsInput {
    organizationId?: string, nameContains?: string, kind?: StoreGroupKindValue, maxResults?: number, nextToken?: string,
}
export interface IGetStoreGroupInfoInput { organizationId?: string, groupId: string }
export interface IListStoreGroupLeafStoresInput {
    organizationId?: string, groupIds: string[], maxResults?: number, nextToken?: string,
}
export interface ICreateStoreGroupInput {
    organizationId?: string, name: string, kind?: StoreGroupKindValue, memberStoreIds?: string[], memberGroupIds?: string[],
}
export interface IUpdateStoreGroupInput {
    organizationId?: string, groupId: string, expectedVersion: number, name?: string,
    addStoreIds?: string[], removeStoreIds?: string[],
}
export interface INestStoreGroupInput {
    organizationId?: string, groupId: string, childGroupId: string, expectedParentVersion: number,
}
export type IDetachStoreGroupInput = INestStoreGroupInput
export interface ICountStoreGroupGrantsInput { organizationId?: string, groupId: string }
export interface IDeleteStoreGroupInput { organizationId?: string, groupId: string, expectedVersion: number }

const VALIDATION_EXCEPTION = 'com.amazon.jwostoremanagementgateway#ValidationException'

function validationError (message: string): Error & { __type: string } {
    return Object.assign(new Error(message), { __type: VALIDATION_EXCEPTION })
}

/**
 * The organizationId for a store-group call, checked and defaulted from one snapshot of the
 * caller's organizations: a supplied id must be one of them (when they are known), and a create
 * from a caller with several organizations must name one rather than fall back to the first.
 * A failed current-user fetch is not retried here; the last known list applies (empty means
 * unchecked, and the gateway still authorizes every call).
 */
async function resolveStoreGroupOrganization (supplied: string | undefined, isCreate = false): Promise<string | undefined> {
    if (!isCurrentUserFailed()) await initCurrentUser()
    const callerOrgIds = getCallerOrganizationIds()
    if (supplied) {
        if (callerOrgIds.length > 0 && !callerOrgIds.includes(supplied)) {
            throw validationError('organizationId is not one of the caller\'s organizations')
        }
        return supplied
    }
    if (isCreate && callerOrgIds.length > 1) {
        throw validationError('organizationId is required to create a store group for a caller with several organizations')
    }
    return getResolvedOrganizationId()
}

export async function listStoreGroups (input: IListStoreGroupsInput): Promise<ListStoreGroupsOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .listStoreGroups(ListStoreGroupsInput.clone({
            organizationId,
            nameContains: input.nameContains,
            kind: input.kind as StoreGroupKind | undefined,
            maxResults: input.maxResults,
            nextToken: input.nextToken,
        }))
        .toPromise()
}

export async function getStoreGroupInfo (input: IGetStoreGroupInfoInput): Promise<GetStoreGroupInfoOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .getStoreGroupInfo(GetStoreGroupInfoInput.clone({ organizationId, groupId: input.groupId }))
        .toPromise()
}

export async function listStoreGroupLeafStores (input: IListStoreGroupLeafStoresInput): Promise<ListStoreGroupLeafStoresOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .listStoreGroupLeafStores(ListStoreGroupLeafStoresInput.clone({
            organizationId,
            groupIds: input.groupIds,
            maxResults: input.maxResults,
            nextToken: input.nextToken,
        }))
        .toPromise()
}

export async function createStoreGroup (input: ICreateStoreGroupInput): Promise<CreateStoreGroupOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId, true)
    return getServiceClient()
        .createStoreGroup(CreateStoreGroupInput.clone({
            organizationId,
            name: input.name,
            kind: input.kind as StoreGroupKind | undefined,
            memberStoreIds: input.memberStoreIds,
            memberGroupIds: input.memberGroupIds,
        }))
        .toPromise()
}

export async function updateStoreGroup (input: IUpdateStoreGroupInput): Promise<UpdateStoreGroupOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .updateStoreGroup(UpdateStoreGroupInput.clone({
            organizationId,
            groupId: input.groupId,
            expectedVersion: input.expectedVersion,
            name: input.name,
            addStoreIds: input.addStoreIds,
            removeStoreIds: input.removeStoreIds,
        }))
        .toPromise()
}

export async function nestStoreGroup (input: INestStoreGroupInput): Promise<NestStoreGroupOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .nestStoreGroup(NestStoreGroupInput.clone({
            organizationId,
            groupId: input.groupId,
            childGroupId: input.childGroupId,
            expectedParentVersion: input.expectedParentVersion,
        }))
        .toPromise()
}

export async function detachStoreGroup (input: IDetachStoreGroupInput): Promise<DetachStoreGroupOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .detachStoreGroup(DetachStoreGroupInput.clone({
            organizationId,
            groupId: input.groupId,
            childGroupId: input.childGroupId,
            expectedParentVersion: input.expectedParentVersion,
        }))
        .toPromise()
}

export async function countStoreGroupGrants (input: ICountStoreGroupGrantsInput): Promise<GetStoreGroupGrantCountOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .getStoreGroupGrantCount(GetStoreGroupGrantCountInput.clone({ organizationId, groupId: input.groupId }))
        .toPromise()
}

export async function deleteStoreGroup (input: IDeleteStoreGroupInput): Promise<DeleteStoreGroupOutput> {
    const organizationId = await resolveStoreGroupOrganization(input.organizationId)
    return getServiceClient()
        .deleteStoreGroup(DeleteStoreGroupInput.clone({
            organizationId,
            groupId: input.groupId,
            expectedVersion: input.expectedVersion,
        }))
        .toPromise()
}
